import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Btn, ConfirmModal, Heading, Modal, Spinner, ErrorState, StatusBadge, PlainText } from '../../components/platform/ui';
import useAsync from '../../hooks/useAsync';
import { platformAdminApi, errorText } from '../../services/platformApi';
import { ImageField, VideoField, Text, Area, DataTable, EnTranslations, initEn, buildTranslations } from './shared';

const EN_SPEC = [
  { key: 'title', label: 'Title', kind: 'text' },
  { key: 'summary', label: 'Summary', kind: 'area', rows: 2 },
  { key: 'body', label: 'Text', kind: 'area', rows: 8 },
];
import VideoPlayer from '../../components/platform/VideoPlayer';
import { formatDateTime } from '../../lib/pay';

const NewsForm = ({ initial, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [f, setF] = useState({ title: '', summary: '', body: '', ...initial, slug: initial?.slug || '', cover_url: initial?.cover_url || '', video_url: initial?.video_url || '', video_aspect: initial?.video_aspect || '16:9' });
  const [en, setEn] = useState(() => initEn(EN_SPEC, initial?.translations));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { title: f.title, slug: f.slug || null, summary: f.summary, body: f.body, cover_url: f.cover_url || null, video_url: f.video_url || null, video_aspect: f.video_url ? f.video_aspect : null, translations: buildTranslations(EN_SPEC, en) };
    setBusy(true);
    try { initial?.id ? await platformAdminApi.updateNews(initial.id, body) : await platformAdminApi.createNews(body); toast.success(t('pl.admin.news.saved')); onSaved(); }
    catch (e) { toast.error(errorText(e, t('pl.admin.prod.saveFail'))); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={initial?.id ? t('pl.admin.news.titleEdit') : t('pl.admin.news.titleNew')}
      footer={(<><Btn variant="secondary" onClick={() => setPreview((v) => !v)}>{preview ? t('pl.admin.news.editor') : t('pl.admin.news.preview')}</Btn><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
      {preview ? (
        <article className="space-y-4">
          {f.video_url
            ? <VideoPlayer url={f.video_url} aspect={f.video_aspect} poster={f.cover_url} />
            : f.cover_url && <img src={f.cover_url} alt="" className="w-full border-2 border-black" />}
          <Heading level={2} className="text-2xl">{f.title || t('pl.admin.news.untitled')}</Heading>
          {f.summary && <p className="text-gray-600">{f.summary}</p>}
          <PlainText text={f.body} />
        </article>
      ) : (
        <>
          <Text label={t('pl.admin.news.heading')} value={f.title} onChange={set('title')} maxLength={200} />
          <Text label={t('pl.admin.news.slug')} value={f.slug} onChange={set('slug')} hint={t('pl.admin.news.slugHint')} disabled={!!initial?.id} />
          <Area label={t('pl.admin.news.summary')} rows={2} value={f.summary} onChange={set('summary')} maxLength={500} />
          <Area label={t('pl.admin.news.body')} rows={10} value={f.body} onChange={set('body')} hint={t('pl.admin.news.bodyHint')} />
          <ImageField label={t('pl.admin.news.cover')} value={f.cover_url} onChange={set('cover_url')} />
          <VideoField value={f.video_url} aspect={f.video_aspect} onChange={set('video_url')} onAspect={set('video_aspect')} poster={f.cover_url} />
          <EnTranslations spec={EN_SPEC} value={en} onChange={setEn} />
        </>
      )}
    </Modal>
  );
};

const AdminNews = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ru').slice(0, 2);
  const { data, loading, error, reload } = useAsync(platformAdminApi.news, []);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errorText(e)); } };
  const remove = async () => {
    setBusy(true);
    try { await platformAdminApi.deleteNews(del.id); toast.success(t('pl.admin.news.deleted')); setDel(null); reload(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3"><Heading level={2} className="text-2xl">{t('pl.admin.news.section')}</Heading><Btn onClick={() => setEdit({})}>{t('pl.admin.news.add')}</Btn></div>
      {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : (
        <DataTable empty={t('pl.admin.news.empty')} rows={data} columns={[
          { label: t('pl.admin.news.colTitle'), render: (n) => <b>{n.title}</b> },
          { label: t('pl.admin.news.colStatus'), render: (n) => n.status === 'published' ? <StatusBadge tone="green">{t('pl.admin.news.statusPublished')}</StatusBadge> : <StatusBadge>{t('pl.admin.news.statusDraft')}</StatusBadge> },
          { label: t('pl.admin.news.colUpdated'), render: (n) => formatDateTime(n.updated_at || n.created_at, lang) },
          { label: '', render: (n) => (
            <div className="flex flex-wrap gap-2">
              <Btn variant="secondary" onClick={() => setEdit(n)}>{t('pl.common.edit')}</Btn>
              {n.status === 'published'
                ? <Btn variant="secondary" onClick={() => act(() => platformAdminApi.unpublishNews(n.id), t('pl.admin.news.unpublished'))}>{t('pl.admin.news.unpublish')}</Btn>
                : <Btn onClick={() => act(() => platformAdminApi.publishNews(n.id), t('pl.admin.news.publishedOk'))}>{t('pl.admin.news.publish')}</Btn>}
              <Btn variant="secondary" onClick={() => setDel(n)}>{t('pl.common.delete')}</Btn>
            </div>) },
        ]} />
      )}
      {edit && <NewsForm initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      <ConfirmModal open={!!del} busy={busy} onClose={() => setDel(null)} onConfirm={remove} confirmLabel={t('pl.common.delete')} text={t('pl.admin.news.deleteConfirm', { title: del?.title })} />
    </div>
  );
};

export default AdminNews;
